import { useEffect, useRef } from 'react';
import type { GameState } from '@pacmates/engine';
import { renderMaze } from './renderMaze';
import { renderEntities } from './renderEntities';
import { effectiveNowMs, readPrefersReducedMotion, resolveEffectiveReducedMotion, type ReducedMotionSetting } from './motion';

export interface GameCanvasProps {
  state: GameState;
  prevState: GameState | null;
  /** performance.now() timestamp the CURRENT snapshot arrived at. */
  receivedAt: number;
  /** approximate ms between snapshots, for the interpolation window. */
  snapshotIntervalMs: number;
  onSwipe?: (dir: 'up' | 'down' | 'left' | 'right') => void;
  /** Exact CSS-px box the wrap element is allowed to occupy, computed by
   * the caller (useGameLayout) from REAL measured HUD/D-pad/safe-area
   * space -- see GameScreen.tsx. When omitted the wrap falls back to
   * filling its parent via CSS (used only before the first layout
   * measurement lands). Inside this box the canvas ALWAYS letterboxes to
   * the maze's true 28x31 aspect ratio -- it never stretches to fill the
   * box and never exceeds it, so an over-generous box just adds blank
   * margin, never clipping (SPEC.md §5). */
  width?: number;
  height?: number;
  /** Solo round: hide player-index-1 on the canvas (the engine still
   * simulates a parked P2 pac; this is display-only). Defaults to true --
   * draw both pacs unless the caller explicitly opts out. */
  drawP2?: boolean;
  /** PART 1: lobby seat display names, for the partner name label (the
   * local player's own label is always "You" -- see renderEntities.ts). */
  p1Name?: string;
  p2Name?: string;
  /** Which seat THIS client is -- decides which pac shows "You". */
  mySeat?: 1 | 2;
  /** PART 3: colorblind-safe palette + non-hue P2 marker -- threaded
   * straight into renderEntities.ts's `pacColor`. Defaults to false. */
  colorblind?: boolean;
  /** PART 4: manual reduced-motion override -- combined with the LIVE OS
   * `prefers-reduced-motion` query (still read here, see the effect below)
   * via `resolveEffectiveReducedMotion` so a manual 'on' freezes decorative
   * motion even when the OS itself reports no-preference, and 'off' keeps
   * it moving even when the OS prefers reduced. Defaults to 'auto' (the
   * pre-existing OS-only behavior). */
  reducedMotionSetting?: ReducedMotionSetting;
}

/** Canvas renderer. Purely reads the authoritative snapshot every animation
 * frame -- never mutates or derives a second copy of game facts (SPEC.md
 * §1.1). Sizes itself to fill its parent container (the parent decides the
 * letterboxed layout budget so every mobile breakpoint fits without page
 * scroll -- see GameScreen.tsx / index.css). */
export function GameCanvas({
  state,
  prevState,
  receivedAt,
  snapshotIntervalMs,
  onSwipe,
  width,
  height,
  drawP2 = true,
  p1Name = 'Player 1',
  p2Name = 'Player 2',
  mySeat = 1,
  colorblind = false,
  reducedMotionSetting = 'auto',
}: GameCanvasProps): JSX.Element {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const wrapRef = useRef<HTMLDivElement | null>(null);
  const rafRef = useRef<number>(0);
  const latest = useRef({
    state,
    prevState,
    receivedAt,
    snapshotIntervalMs,
    drawP2,
    p1Name,
    p2Name,
    mySeat,
    colorblind,
    reducedMotionSetting,
  });
  latest.current = {
    state,
    prevState,
    receivedAt,
    snapshotIntervalMs,
    drawP2,
    p1Name,
    p2Name,
    mySeat,
    colorblind,
    reducedMotionSetting,
  };

  useEffect(() => {
    const canvas = canvasRef.current;
    const wrap = wrapRef.current;
    if (!canvas || !wrap) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    function resize(): GhostSize {
      const { state: s } = latest.current;
      const dpr = window.devicePixelRatio || 1;
      const availW = wrap!.clientWidth;
      const availH = wrap!.clientHeight;
      const tile = Math.max(4, Math.min(availW / s.cols, availH / s.rows));
      const cssW = tile * s.cols;
      const cssH = tile * s.rows;
      canvas!.style.width = `${cssW}px`;
      canvas!.style.height = `${cssH}px`;
      canvas!.width = Math.round(cssW * dpr);
      canvas!.height = Math.round(cssH * dpr);
      ctx!.setTransform(dpr, 0, 0, dpr, 0, 0);
      return { tile };
    }

    let size = resize();
    const ro = new ResizeObserver(() => {
      size = resize();
    });
    ro.observe(wrap);

    // Hard constraint (plan-gen1.md #3): prefers-reduced-motion must freeze
    // all canvas-driven decorative animation (glow pulse, fright-flash
    // blink). The canvas can't read the CSS media query implicitly, so we
    // read it here on mount + on 'change' and thread the resulting boolean
    // into the render loop as a frozen decorative clock -- see game/motion.ts.
    // PART 4: this is the OS-only signal -- the EFFECTIVE freeze decision
    // (which also considers `latest.current.reducedMotionSetting`, a manual
    // override) is computed fresh every frame below via
    // `resolveEffectiveReducedMotion`, so a settings change takes effect on
    // the very next frame without needing to re-subscribe anything here.
    let osPrefersReducedMotion = readPrefersReducedMotion();
    const mql = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(prefers-reduced-motion: reduce)') : null;
    const onMotionChange = (e: MediaQueryListEvent): void => {
      osPrefersReducedMotion = e.matches;
    };
    mql?.addEventListener('change', onMotionChange);

    function frame(): void {
      const {
        state: s,
        prevState: p,
        receivedAt: ra,
        snapshotIntervalMs: interval,
        drawP2: dp2,
        p1Name: n1,
        p2Name: n2,
        mySeat: seat,
        colorblind: cb,
        reducedMotionSetting: rmSetting,
      } = latest.current;
      const now = performance.now();
      // PART 4: real server motion, NEVER frozen -- `frac` is the
      // interpolation fraction between two ALREADY-authoritative snapshot
      // positions (SPEC.md §1.1), not a decorative effect. Freezing it
      // would make every pac/ghost visibly teleport tick-to-tick instead of
      // gliding, which is a worse experience and not what "reduced motion"
      // asks for -- only the genuinely cosmetic clock below (glow pulse,
      // fright-flash blink, ghost skirt hem, mouth phase) is gated.
      const frac = interval > 0 ? Math.min(1, (now - ra) / interval) : 1;
      const effectiveReduced = resolveEffectiveReducedMotion(rmSetting, osPrefersReducedMotion);
      const decorativeNow = effectiveNowMs(now, effectiveReduced);
      renderMaze(ctx!, s, size.tile, decorativeNow);
      renderEntities(ctx!, s, p, size.tile, frac, decorativeNow, dp2, n1, n2, seat, cb);
      rafRef.current = requestAnimationFrame(frame);
    }
    // DEFECT 20 (secondary cause, measured): resize() sizes the canvas
    // synchronously, but the actual DRAWING only happened inside frame(),
    // deferred to the NEXT requestAnimationFrame callback -- between mount
    // and that first rAF firing, the canvas was genuinely blank (its
    // default transparent/black backing), not just obscured by CSS. This
    // was intermittent: a screenshot taken in that narrow window (the exact
    // moment CountdownOverlay attaches, on the SAME render as this canvas
    // first mounts) caught a real void, independent of and in addition to
    // the `.overlay` scrim-opacity bug (index.css) -- a fresh measurement
    // run reproduced both a fully-painted capture AND a near-empty one
    // across otherwise-identical runs. Paint the first frame SYNCHRONOUSLY
    // right here so the canvas never has an unpainted frame to be
    // screenshotted, then let frame() take over the normal rAF loop from
    // its own trailing requestAnimationFrame(frame) call.
    frame();

    return () => {
      cancelAnimationFrame(rafRef.current);
      ro.disconnect();
      mql?.removeEventListener('change', onMotionChange);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const touchStart = useRef<{ x: number; y: number } | null>(null);

  function onTouchStart(e: React.TouchEvent): void {
    const t = e.touches[0];
    if (t) touchStart.current = { x: t.clientX, y: t.clientY };
  }
  function onTouchEnd(e: React.TouchEvent): void {
    const start = touchStart.current;
    touchStart.current = null;
    if (!start || !onSwipe) return;
    const t = e.changedTouches[0];
    if (!t) return;
    const dx = t.clientX - start.x;
    const dy = t.clientY - start.y;
    const SWIPE_MIN = 24;
    if (Math.abs(dx) < SWIPE_MIN && Math.abs(dy) < SWIPE_MIN) return;
    if (Math.abs(dx) > Math.abs(dy)) {
      onSwipe(dx > 0 ? 'right' : 'left');
    } else {
      onSwipe(dy > 0 ? 'down' : 'up');
    }
  }

  const wrapStyle: React.CSSProperties | undefined =
    width != null && height != null ? { width: `${width}px`, height: `${height}px` } : undefined;

  return (
    <div ref={wrapRef} className="game-canvas-wrap" data-testid="game-canvas-wrap" style={wrapStyle}>
      <canvas
        ref={canvasRef}
        className="game-canvas"
        role="img"
        aria-label="Pac-Man co-op maze"
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      />
    </div>
  );
}

interface GhostSize {
  tile: number;
}
